import { Box3, BufferGeometry, Mesh, Object3D, Vector3 } from "three";

interface MillingPriceCalculation {
  bboxVolume: number; // cm³
  partVolume: number; // cm³
  removalVolume: number; // cm³
  materialCost: number;
  machineTime: number; // hours
  setupCost: number;
  totalCost: number;
  triangleCount: number; // Add triangle count
}

interface MillingMaterial {
  name: string;
  costPerKg: number;
  density: number; // g/cm³
  removalRateFactor: number; // Relative rate (higher is faster)
}

// Define available milling materials
const MILLING_MATERIALS: Record<string, MillingMaterial> = {
  "Aluminum 6061": {
    name: "Aluminum 6061",
    costPerKg: 8,
    density: 2.7,
    removalRateFactor: 1.0,
  },
  "Steel 304": {
    name: "Steel 304",
    costPerKg: 12,
    density: 8.0,
    removalRateFactor: 0.6,
  },
  "Titanium Gr5": {
    name: "Titanium Gr5",
    costPerKg: 70,
    density: 4.43,
    removalRateFactor: 0.4,
  },
  PEEK: { name: "PEEK", costPerKg: 100, density: 1.32, removalRateFactor: 1.2 },
};

const DEFAULT_MILLING_MATERIAL_NAME = "Aluminum 6061";

const MACHINE_RATE_PER_HOUR = 60;
const SETUP_COST_BASE = 50;
const SETUP_COST_PER_100K_TRIS = 10;
const BASE_REMOVAL_RATE_CM3_PER_HOUR = 200; // For factor = 1.0
const MIN_MACHINE_TIME_HOURS = 0.3; // Reduced slightly

/**
 * Calculates the bounding box volume of an Object3D in cubic centimeters.
 * Assumes the object has already been scaled appropriately (e.g., 1 unit = 1 mm).
 */
function calculateBoundingBoxVolumeCm3(model: Object3D): number {
  const box = new Box3().setFromObject(model);
  if (box.isEmpty()) {
    return 0;
  }
  const size = new Vector3();
  box.getSize(size);
  // Assuming size is in mm, convert to cm³
  return (size.x / 10) * (size.y / 10) * (size.z / 10);
}

/**
 * Calculates the total volume of all meshes within a Group in cubic centimeters.
 * Assumes geometry units are mm.
 */
function calculateMeshGroupVolumeCm3(modelGroup: Object3D): {
  volume: number;
  triangleCount: number;
} {
  let totalVolume = 0;
  let totalTriangles = 0;

  modelGroup.traverse((child) => {
    if (child instanceof Mesh && child.geometry instanceof BufferGeometry) {
      const geometry = child.geometry;
      const positions = geometry.attributes.position?.array;
      const indices = geometry.index?.array;

      if (!positions) return;

      let meshVolume = 0;
      let meshTriangles = 0;

      if (indices) {
        meshTriangles = indices.length / 3;
        for (let i = 0; i < indices.length; i += 3) {
          const i1 = indices[i] * 3;
          const i2 = indices[i + 1] * 3;
          const i3 = indices[i + 2] * 3;

          const v1 = new Vector3(
            positions[i1],
            positions[i1 + 1],
            positions[i1 + 2]
          );
          const v2 = new Vector3(
            positions[i2],
            positions[i2 + 1],
            positions[i2 + 2]
          );
          const v3 = new Vector3(
            positions[i3],
            positions[i3 + 1],
            positions[i3 + 2]
          );

          // Signed volume of tetrahedron (origin -> v1 -> v2 -> v3)
          meshVolume += v1.dot(v2.cross(v3)) / 6.0;
        }
      } else {
        // Non-indexed geometry
        meshTriangles = positions.length / 9;
        for (let i = 0; i < positions.length; i += 9) {
          const v1 = new Vector3(
            positions[i],
            positions[i + 1],
            positions[i + 2]
          );
          const v2 = new Vector3(
            positions[i + 3],
            positions[i + 4],
            positions[i + 5]
          );
          const v3 = new Vector3(
            positions[i + 6],
            positions[i + 7],
            positions[i + 8]
          );
          meshVolume += v1.dot(v2.cross(v3)) / 6.0;
        }
      }
      // Add absolute volume (geometry might have inverted normals)
      // Convert mm³ to cm³
      totalVolume += Math.abs(meshVolume) / 1000.0;
      totalTriangles += meshTriangles;
    }
  });

  return { volume: totalVolume, triangleCount: totalTriangles };
}

/**
 * Estimates milling time based on material removal volume and material factor.
 */
function estimateMillingTime(
  removalVolumeCm3: number,
  materialFactor: number
): number {
  if (removalVolumeCm3 <= 0) return MIN_MACHINE_TIME_HOURS;
  const effectiveRemovalRate = BASE_REMOVAL_RATE_CM3_PER_HOUR * materialFactor;
  if (effectiveRemovalRate <= 0) return 999; // Avoid division by zero, return large time
  const time = removalVolumeCm3 / effectiveRemovalRate;
  return Math.max(MIN_MACHINE_TIME_HOURS, time);
}

export function calculateMillingCosts(
  model: Object3D,
  materialName: string = DEFAULT_MILLING_MATERIAL_NAME // Accept material name
): MillingPriceCalculation {
  const material =
    MILLING_MATERIALS[materialName] ||
    MILLING_MATERIALS[DEFAULT_MILLING_MATERIAL_NAME]; // Look up material

  const bboxVolume = calculateBoundingBoxVolumeCm3(model);
  const { volume: partVolume, triangleCount } =
    calculateMeshGroupVolumeCm3(model);
  const removalVolume = Math.max(0, bboxVolume - partVolume);

  // --- Costs ---

  // 1. Material Cost (based on stock/bbox)
  const massKg = (bboxVolume * material.density) / 1000;
  const materialCost = massKg * material.costPerKg;

  // 2. Machining Cost (based on removal volume)
  const machineTime = estimateMillingTime(
    removalVolume,
    material.removalRateFactor
  ); // Use factor
  const machineCost = machineTime * MACHINE_RATE_PER_HOUR;

  // 3. Setup Cost (base + complexity factor)
  const setupComplexityCost =
    Math.floor(triangleCount / 100000) * SETUP_COST_PER_100K_TRIS;
  const setupCost = SETUP_COST_BASE + setupComplexityCost;

  // --- Total ---
  const totalCost = materialCost + machineCost + setupCost;

  return {
    bboxVolume,
    partVolume,
    removalVolume,
    triangleCount,
    materialCost,
    machineTime,
    setupCost,
    totalCost,
  };
}

// Helper to get available material names
export function getAvailableMillingMaterials(): string[] {
  return Object.keys(MILLING_MATERIALS);
}
